import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { CreateFigureVersionInput, CropInput, Figure, FigureVersion, Image } from '@graphql/types';
import { useStore } from '@stores/rootStoreContext';
import { useCreateFigureVersionMutation } from '@components/Publication/Form/gql/PublicationEditor.generated';
import SimpleModal from '../../UI/SimpleModal/SimpleModal';
import ImageCropper from '../../UI/ImageEditor/ImageCropper/ImageCropper';
import DeleteIcon from '../../UI/Icons/DeleteIcon';
import { CropperType } from '../../../types/CropperType';
import { TopIncutBlockModalProps } from './StoryIncutBlockModalProps';
import { TOP_INCUT_BLOCK_MODAL_CONSTS } from './StoryIncutBlockModal.consts';
import { ConfirmationTextWrapper, DeleteButtonWrapper } from './StoryIncutBlockModal.styles';

const StoryIncutBlockModal: FC<TopIncutBlockModalProps> = ({ figureVersion, open, setIsModalOpen, setFigureVersion, incutUid }) => {
    const [imageUrl, setImageUrl] = useState<string>('');
    const [currFigure, setCurrFigure] = useState<Figure>({} as Figure);
    const { cropperStore, storiesIncutStore } = useStore();
    const { cropper, isCropperChanged, setCropper } = cropperStore;
    const { addingBlocksCache, storiesIncutBlocks, setStoriesIncutBlocks } = storiesIncutStore;
    const [isModalDirty, setIsModalDirty] = useState<boolean>(false);
    const [disabledFormState, setDisabledFormState] = useState<boolean>(true);
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState<boolean>(false);

    const [createFigureVersion, { loading: createFigureVersionLoading }] = useCreateFigureVersionMutation();

    const createFigureVersionAction = useCallback(
        (createFigureVersionInputData: CreateFigureVersionInput) => {
            return createFigureVersion({
                variables: {
                    data: createFigureVersionInputData,
                },
                update(cache, { data }) {
                    if (data?.createFigureVersion?.figureVersion && incutUid) {
                        const newFigureVersion = data.createFigureVersion.figureVersion;
                        cache.modify({
                            id: cache.identify({ __typename: 'StoriesIncutBlock', uid: incutUid }),
                            fields: {
                                figureVersion() {
                                    return newFigureVersion;
                                },
                            },
                        });
                    }
                },
            });
        },
        [createFigureVersion, incutUid]
    );

    useEffect(() => {
        const newFigure: Figure = {
            hasDiagonalWatermark: figureVersion?.hasDiagonalWatermark || false,
            hasWatermark: figureVersion?.hasWatermark || false,
            sourceImage: figureVersion?.figure?.sourceImage || ({} as Image),
            cropperPreview: figureVersion?.figure?.cropperPreview || ({} as Image),
            id: figureVersion?.figure?.id || '',
            latestVersion: figureVersion?.figure?.latestVersion || ({} as FigureVersion),
            src: figureVersion?.figure?.src || '',
            type: figureVersion?.figure?.type || 'story-incut',
            uid: figureVersion?.figure?.uid || '',
        } as Figure;
        setCurrFigure(newFigure);
        setImageUrl(figureVersion?.figure?.latestVersion?.primaryProcessedImage?.url || '');
        setCropper((figureVersion?.crop as CropInput) || null);
    }, [figureVersion, setCropper]);

    useEffect(() => {
        const newDirtyVal =
            isCropperChanged ||
            cropper?.width !== figureVersion?.crop?.width ||
            cropper?.height !== figureVersion?.crop?.height ||
            cropper?.left !== figureVersion?.crop?.left ||
            cropper?.top !== figureVersion?.crop?.top ||
            imageUrl !== figureVersion?.primaryProcessedImage?.url;

        setIsModalDirty(newDirtyVal);
    }, [cropper, isCropperChanged, figureVersion, imageUrl]);

    useEffect(() => {
        setDisabledFormState(!currFigure?.uid || !isModalDirty);
        if (currFigure?.latestVersion?.primaryProcessedImage?.url) {
            setImageUrl(currFigure?.latestVersion?.primaryProcessedImage?.url || '');
        }
    }, [currFigure, isModalDirty]);

    const clearFigure = useCallback(() => {
        setCurrFigure({} as Figure);
        setDisabledFormState(true);
        setImageUrl('');
        setIsModalDirty(true);
    }, []);

    const resetForm = useCallback(() => {
        setCurrFigure(figureVersion.figure || ({} as Figure));
        setImageUrl(figureVersion?.figure?.latestVersion?.primaryProcessedImage?.url || '');
        setCropper((figureVersion?.crop as CropInput) || null);
        setDisabledFormState(true);
        setIsModalDirty(false);
    }, [figureVersion, setCropper]);

    const closeModalHandler = useCallback(() => {
        if (isModalDirty && currFigure?.uid) {
            setIsOpenConfirmDialog(true);
        } else {
            setIsModalOpen(false);
        }
    }, [isModalDirty, setIsModalOpen, isOpenConfirmDialog]);

    const closeModal = useCallback(() => {
        if (isOpenConfirmDialog) {
            resetForm();
        }
        setIsOpenConfirmDialog(false);
        setIsModalDirty(false);
        setIsModalOpen(false);
    }, [isOpenConfirmDialog, setIsModalOpen, resetForm]);

    const handleOk = useCallback(async () => {
        if (!currFigure?.uid) {
            // Если нет изображения, сохраняем пустую figureVersion
            if (addingBlocksCache.has(incutUid)) {
                const existingBlock = addingBlocksCache.get(incutUid) as StoriesIncutBlock;
                addingBlocksCache.set(incutUid, { ...existingBlock, figureVersion: null });
                setStoriesIncutBlocks([
                    ...addingBlocksCache.values(),
                    ...storiesIncutBlocks.filter((b) => !addingBlocksCache.has(b.uid)),
                ]);
                console.log('Updated addingBlocksCache with figureVersion: null for incut:', incutUid);
            }
            closeModal();
            return;
        }

        try {
            const newFigureVersion = await createFigureVersionAction({
                figureId: currFigure?.id,
                hasWatermark: currFigure.hasWatermark,
                hasDiagonalWatermark: currFigure.hasDiagonalWatermark,
                crop: cropper,
            } as CreateFigureVersionInput);

            if (!newFigureVersion?.errors?.length) {
                const savedFigureVersion = newFigureVersion.data?.createFigureVersion?.figureVersion as FigureVersion;
                setFigureVersion(savedFigureVersion);
                // Обновляем addingBlocksCache и storiesIncutBlocks
                if (addingBlocksCache.has(incutUid)) {
                    const existingBlock = addingBlocksCache.get(incutUid) as StoriesIncutBlock;
                    addingBlocksCache.set(incutUid, { ...existingBlock, figureVersion: savedFigureVersion });
                    setStoriesIncutBlocks([
                        ...addingBlocksCache.values(),
                        ...storiesIncutBlocks.filter((b) => !addingBlocksCache.has(b.uid)),
                    ]);
                    console.log('Updated addingBlocksCache with figureVersion for incut:', incutUid, savedFigureVersion);
                }
                closeModal();
            } else {
                console.error('GraphQL errors:', newFigureVersion.errors);
            }
        } catch (e) {
            console.error('Error saving figure version:', e);
        }
    }, [currFigure, cropper, setFigureVersion, closeModal, createFigureVersionAction, incutUid, addingBlocksCache, storiesIncutBlocks, setStoriesIncutBlocks]);

    return (
        <SimpleModal
            open={open}
            onClose={closeModalHandler}
            onOk={handleOk}
            title={TOP_INCUT_BLOCK_MODAL_CONSTS.titleMsg}
            okLabel={TOP_INCUT_BLOCK_MODAL_CONSTS.addPhotoMsg}
            okButtonDisabled={createFigureVersionLoading}
            loading={createFigureVersionLoading}
            onNo={closeModalHandler}
        >
            <Box>
                <ImageCropper
                    hasDiagonalWatermark={currFigure?.latestVersion?.figure?.hasDiagonalWatermark || false}
                    hasWatermark={currFigure?.latestVersion?.figure?.hasWatermark || false}
                    figure={currFigure}
                    setFigure={setCurrFigure}
                    imageUrl={imageUrl}
                    setImageUrl={setImageUrl}
                    ratio={16 / 9}
                    isCustomRatio={true}
                    figureTypeSlug="story-incut"
                    cropperType={CropperType.isTopNewsBlock}
                />
                {currFigure?.src && (
                    <DeleteButtonWrapper>
                        <Tooltip title={TOP_INCUT_BLOCK_MODAL_CONSTS.setAnotherPhotoLabelMsg}>
                            <IconButton edge="end" size="small" id="top-incut-image-modal-delete-photo" onClick={clearFigure}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </DeleteButtonWrapper>
                )}
            </Box>

            <SimpleModal
                open={isOpenConfirmDialog}
                mode="alert"
                variant="error"
                title={TOP_INCUT_BLOCK_MODAL_CONSTS.warningMsg}
                okLabel={TOP_INCUT_BLOCK_MODAL_CONSTS.closeConfirmBtn}
                yesLabel={TOP_INCUT_BLOCK_MODAL_CONSTS.closeConfirmBtn}
                onOk={closeModal}
                onYes={closeModal}
                onClose={() => setIsOpenConfirmDialog(false)}
                onNo={() => setIsOpenConfirmDialog(false)}
            >
                <ConfirmationTextWrapper>
                    <Typography variant="h2">{TOP_INCUT_BLOCK_MODAL_CONSTS.backConfirmationText}</Typography>
                </ConfirmationTextWrapper>
            </SimpleModal>
        </SimpleModal>
    );
};

export default observer(StoryIncutBlockModal);